import { FC, Fragment, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import WidgetContainer from "../../UiLib/WidgetContainer/WidgetContainer";
import "./IntegrationTasksTree.css";
import {
    ExternalPlanElement,
    ExternalPlanElementTypes,
} from "../../../common/api_interfaces/project/ExternalPlanElement";
import { TaskContext } from "../../../contexts/TaskContext";
import { isAxiosError } from "axios";
import { useUI } from "../../../contexts/UiContext";
import LineConnector from "../TaskRightInfo/SubTasksGraph/LineConnector";
import ArrowDown from "../../../assets/arrowDown.svg";
import ArrowRight from "../../../assets/ArrowRight.svg";
import SubTasks from "../../../assets/subtasks.svg";
import { ITaskEdit } from "../../../common/api_interfaces/task/ITaskEdit";
import { Context } from "../../..";
import { dateToNiceString } from "../../../helpers/dateToNiceString";
import Search from "../../UiLib/Search/Search";
import ExitCross from "../../../assets/cancelGreey.svg";

export interface ExternalPlanElementExtended extends ExternalPlanElement {
    subtasks: ExternalPlanElementExtended[];
}

const LEVEL_COLORS = ["#F97316", "#699F4D", "#C25ADB", "#6974EB", "#C9D6FC", "#F5D5E0", "#F7EBFC"];

interface IntegrationTasksTreeProps {
    tasks: ExternalPlanElementExtended[];
    onLink: () => void;
    onClose: () => void;
}

const IntegrationTasksTree: FC<IntegrationTasksTreeProps> = ({ tasks, onLink, onClose }) => {
    const [foldedTaskIds, setFoldedTaskIds] = useState<Set<string>>(new Set());
    const { taskInformation } = useContext(TaskContext);
    const { store } = useContext(Context);
    const { showToast } = useUI();
    const [filteredTasks, setFilteredTasks] = useState<ExternalPlanElementExtended[]>(tasks);

    const [graphOffset, setGraphOffset] = useState<[number, number]>([0, 0]);
    const [graphParentHeight, setGraphParentHeight] = useState<number>(0);
    const tableRef = useRef<HTMLTableElement>(null);
    const [graphUpdateFlag, setGraphUpdateFlag] = useState<boolean>(true);
    const [selectedTask, setSelectedTask] = useState<ExternalPlanElementExtended>();
    const [searchValue, setSearchValue] = useState<string>("");

    const editExternalId = async () => {
        if (!selectedTask || !taskInformation) return;

        try {
            const editReq: ITaskEdit = {
                id: taskInformation.id,
                externalId: selectedTask.task?.id,
            };

            await store.taskService.editTask(editReq);
            onLink();
        } catch (err) {
            if (isAxiosError(err)) {
                showToast(err.response?.data.message);
            }
        }
    };

    const foldTask = useCallback((taskId: string) => {
        setFoldedTaskIds((prev) => new Set(prev).add(taskId));
        setGraphUpdateFlag(!graphUpdateFlag);
    }, []);

    const unfoldTask = useCallback((taskId: string) => {
        setFoldedTaskIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
        });
        setGraphUpdateFlag(!graphUpdateFlag);
    }, []);

    const isFolded = (id: string) => foldedTaskIds.has(id);

    const updateDimensions = () => {
        if (!tableRef.current) return;

        const parentRect = tableRef.current.getBoundingClientRect();
        setGraphOffset([-parentRect.left, -parentRect.top]);
        setGraphParentHeight(parentRect.height);
    };

    useEffect(() => {
        if (!tableRef.current) return;

        updateDimensions();

        const observer = new ResizeObserver(updateDimensions);
        observer.observe(tableRef.current);

        return () => observer.disconnect();
    }, [tableRef]);

    const onSearch = () => {
        setFilteredTasks(searchTree(tasks, searchValue));
    };

    function searchTree(nodes: ExternalPlanElementExtended[], query: string): ExternalPlanElementExtended[] {
        const q = query.trim().toLowerCase();
        if (!q) return nodes; // если строка пустая — возвращаем всё

        const searchNode = (node: ExternalPlanElementExtended): ExternalPlanElementExtended | null => {
            const nameMatches = node.elementName.toLowerCase().includes(q);

            // фильтруем детей
            const filteredChildren = node.subtasks
                .map(searchNode)
                .filter((child): child is ExternalPlanElementExtended => child !== null);

            // если нода совпадает или есть совпавшие дети — оставляем
            if (nameMatches || filteredChildren.length > 0) {
                return {
                    ...node,
                    subtasks: filteredChildren,
                };
            }

            return null;
        };

        return nodes.map(searchNode).filter((n): n is ExternalPlanElementExtended => n !== null);
    }

    const renderTask = useCallback(
        (index: number, task: ExternalPlanElementExtended, level: number, parentTaskId?: string): ReactNode => {
            const hasSubtasks = task.subtasks.length > 0;
            const folded = isFolded(task.elementId);
            const color = LEVEL_COLORS[level % LEVEL_COLORS.length];
            const groupTask = task.elementType === ExternalPlanElementTypes.group;

            return (
                <Fragment key={`task-${index}`}>
                    <tr
                        className={`task-row ${selectedTask?.elementId === task.elementId ? "selected" : ""} ${
                            groupTask ? "group" : ""
                        }`}
                        style={{ width: "100%" }}
                        onClick={() => {
                            if (!groupTask) setSelectedTask(task);
                        }}
                    >
                        <td style={{ width: "50%", minWidth: `${100 + 15 * level}px`, borderRadius: "5px 0px 0px 5px" }}>
                            <div style={{ display: "flex", alignItems: "center", height: "fit-content" }}>
                                {parentTaskId && (
                                    <LineConnector
                                        offset={graphOffset}
                                        parentHeight={graphParentHeight}
                                        sourceId={`subtask-${parentTaskId}`}
                                        targetId={`subtask-${task.elementId}`}
                                        updateFlag={graphUpdateFlag}
                                    />
                                )}

                                <div
                                    style={{
                                        flexShrink: 0,
                                        display: "flex",
                                        marginLeft: `${15 * level}px`,
                                        width: "fit-content",
                                    }}
                                >
                                    <div>
                                        <svg
                                            width="10"
                                            height="10"
                                            style={{
                                                verticalAlign: "baseline",
                                                marginRight: "8px",
                                                width: "10px",
                                                height: "10px",
                                            }}
                                        >
                                            <circle
                                                id={`subtask-${task.elementId}`}
                                                cx="4"
                                                cy="4"
                                                r="4"
                                                fill="#E1E7EA"
                                            />
                                        </svg>
                                    </div>
                                    {hasSubtasks ? (
                                        <button
                                            className="subtask-hide-button"
                                            onClick={(e) => {
                                                folded ? unfoldTask(task.elementId) : foldTask(task.elementId);
                                                e.stopPropagation();
                                            }}
                                            aria-label={folded ? "Развернуть" : "Свернуть"}
                                        >
                                            <img
                                                alt="arrow"
                                                width="20px"
                                                style={{ marginLeft: "-10px" }}
                                                src={folded ? ArrowDown : ArrowRight}
                                            />
                                        </button>
                                    ) : (
                                        <div style={{ width: "20px", marginLeft: "-10px" }} />
                                    )}
                                    <div className="subtask-number-block">
                                        <div className="subtask-number-block-cur">
                                            <svg
                                                width="10"
                                                height="10"
                                                style={{ verticalAlign: "baseline", padding: "1px" }}
                                            >
                                                <circle cx="4" cy="4" r="4" fill={color} />
                                            </svg>

                                            <span style={{ width: "fit-content" }}>{task.elementName}</span>
                                        </div>
                                        {hasSubtasks && (
                                            <div className="subtask-number-text-block">
                                                <span className="subtask-subtasks-number-text">
                                                    {task.subtasks.length}
                                                </span>
                                                <img src={SubTasks} alt="subtasks" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td>{task.task?.executor?.name}</td>
                        <td style={{textAlign: "center"}}>{task.task?.start && dateToNiceString(new Date(task.task.start), "dd.MM.yy")}</td>
                        <td style={{textAlign: "center"}}>
                            {task.task?.end && dateToNiceString(new Date(task.task.end), "dd.MM.yy")}
                        </td>
                        <td style={{borderRadius: "0px 5px 5px 0px", textAlign: "center"}}>{task.task?.status}</td>
                    </tr>
                    {!folded &&
                        task.subtasks.map((subTask, index) =>
                            renderTask(index + 1, subTask, level + 1, task.elementId)
                        )}
                </Fragment>
            );
        },
        [graphOffset, graphParentHeight, isFolded, foldTask, unfoldTask, graphUpdateFlag]
    );

    return (
        <WidgetContainer className="integration-tree-container">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="modal-title">Добавление задачи</span>
                <button className="button-close" onClick={onClose}>
                    <img style={{ width: "18.7px", height: "18.7px" }} src={ExitCross} alt="Close" />
                </button>
            </div>
            <div>
                <div>Поиск задачи</div>
                <Search value={searchValue} onSubmitFunc={onSearch} changeSearchValue={(val) => setSearchValue(val)} />
            </div>
            <div className="integration-tasks-table">
                <table style={{ position: "relative", borderCollapse: "collapse", width: "100%"}} ref={tableRef}>
                    <tr style={{ width: "100%" }}>
                        <th style={{ width: "50%" }}>Название</th>
                        <th style={{ width: "20%" }}>Ответственный</th>
                        <th style={{ width: "10%" }}>Дата начала</th>
                        <th style={{ width: "10%" }}>Дата окончания</th>
                        <th style={{ width: "10%" }}>Статус</th>
                    </tr>
                    {filteredTasks.map((task) => renderTask(0, task, 0))}
                </table>
            </div>
            <div style={{ display: "flex", justifyContent: "end", gap: "12px" }}>
                <div className="task__create--edit__form--tab__panel--button__add">
                    <button style={{ background: "none", border: "none" }} onClick={editExternalId}>
                        Сохранить
                    </button>
                </div>
                <div className="task__create--edit__form--tab__panel--button__close">
                    <button style={{ background: "none", border: "none" }} onClick={onClose}>
                        Отмена
                    </button>
                </div>
            </div>
        </WidgetContainer>
    );
};

export default IntegrationTasksTree;
